import styled from '@emotion/styled';
import { Grid, Box, Typography, Button, IconButton } from '@mui/material';
import { EditIcon } from '@/icons/EditIcon';
import { TipsModeChip } from '@/components/shared/TipsModeChip';
import { TipsOperationMode } from '@/pages/company/tips/TipsEnableModal';
import Chip from '@/components/shared/Chip';
import { TypographyContainer } from '@/components/shared/InputText';
import { FONT_WEIGHT, TEXT_SIZE } from '@/typography/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ReactNode, useMemo } from 'react';
import { ExclamationMarkIcon } from '@/icons/ExclamationMarkIcon';
import { useTranslation } from 'react-i18next';
import { ConnectionStatus } from '@/services/PaymentsServiceV2';

interface ConnectStatusProps {
    icon?: JSX.Element | null | undefined;
    status?: ConnectionStatus;
    title?: string;
    id?: string;
    subTitle?: string;
    handleEdit: () => void;
    handleEditIntegration: () => void;
    displayEdit?: boolean;
    allowConnect?: boolean;
    url?: any;
    useUrl?: boolean;
    errorMessage?: string;
    connectionString?: string;
    tipsMode?: TipsOperationMode;
    onChipClick?: () => void;
    // Provider-specific action rendered just left of the status chip.
    leadingAction?: ReactNode;
}

export default function ConnectStatus({
    icon,
    status,
    title,
    id,
    subTitle,
    handleEdit,
    handleEditIntegration,
    displayEdit,
    allowConnect = true,
    url,
    useUrl,
    errorMessage,
    connectionString,
    tipsMode,
    onChipClick,
    leadingAction
}: ConnectStatusProps) {
    const chipColor = useMemo(() => {
        if (errorMessage) {
            return 'error';
        }
        if (status == 'ConnectionError') {
            return 'error';
        }
        if (connectionString) {
            return 'success';
        }
        if (status === 'Connected') {
            return 'success';
        }
        return 'muted' as any;
    }, [status, errorMessage, connectionString]);

    const { t } = useTranslation();

    const statusMessage = useMemo(() => {
        if (errorMessage) {
            return errorMessage;
        }
        if (connectionString) {
            return connectionString;
        }
        if (status === 'ConnectionError') {
            return t('connection_error');
        }
        if (status === 'Connected') {
            return t('connected');
        }
        return t('not_connected');
    }, [status, errorMessage, connectionString, t]);

    return (
        <>
            <Grid container justifyContent={'space-between'} display={'flex'}>
                <Grid
                    item
                    xs={5}
                    display={'flex'}
                    justifyContent={'flex-start'}
                >
                    <Grid item marginRight={2}>
                        {errorMessage ? <ExclamationMarkIcon /> : icon}
                    </Grid>
                    <Box
                        maxWidth={'90%'}
                        whiteSpace={'nowrap'}
                        overflow={'hidden'}
                        textOverflow={'ellipsis'}
                    >
                        <TypographyContainer
                            fontWeight={700}
                            fontSize={TEXT_SIZE.MEDIUM_SMALL}
                            overflow={'hidden'}
                            whiteSpace={'nowrap'}
                            textOverflow={'ellipsis'}
                        >
                            {title}
                            {id && (
                                <span
                                    style={{
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        display: 'inline'
                                    }}
                                >
                                    {' '}
                                    - {id}
                                </span>
                            )}
                        </TypographyContainer>
                        <Typography
                            color={'#667085'}
                            fontSize={TEXT_SIZE.MEDIUM_SMALL}
                            overflow={'hidden'}
                            whiteSpace={'nowrap'}
                            textOverflow={'ellipsis'}
                        >
                            {subTitle}
                        </Typography>
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={7}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            mr: 1.5
                        }}
                    >
                        {leadingAction}
                        {tipsMode ? (
                            <TipsModeChip
                                mode={tipsMode}
                                onClick={onChipClick}
                                size="small"
                            />
                        ) : (
                            <Chip
                                icon={<span></span>}
                                label={
                                    <Typography
                                        fontWeight={FONT_WEIGHT.MEDIUM}
                                        fontSize={'14px'}
                                        lineHeight={'20px'}
                                        sx={{
                                            overflow: 'visible',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {errorMessage ?? statusMessage}
                                    </Typography>
                                }
                                color={chipColor}
                                onClick={onChipClick}
                                sx={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    minHeight: '32px',
                                    cursor: onChipClick ? 'pointer' : 'default',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        padding: '6px 12px'
                                    }
                                }}
                            />
                        )}
                    </Box>
                    {
                        <IconButton
                            onClick={handleEditIntegration}
                            style={{ display: displayEdit ? 'block' : 'none' }}
                        >
                            {' '}
                            <EditIcon />
                        </IconButton>
                    }
                    <IconButton
                        style={{
                            display: displayEdit ? 'none' : 'block',
                            pointerEvents: 'none',
                            padding: '8px 11px'
                        }}
                    >
                        {' '}
                        <Box sx={{ padding: '8px' }} />
                    </IconButton>

                    {allowConnect ? (
                        <IconButton onClick={handleEdit}>
                            {useUrl ? (
                                <a
                                    href={url}
                                    style={{
                                        color: 'white',
                                        textDecoration: 'none'
                                    }}
                                >
                                    <ChevronRightIcon color={'secondary'} />
                                </a>
                            ) : (
                                <ChevronRightIcon color={'secondary'} />
                            )}
                        </IconButton>
                    ) : (
                        <IconButton style={{ visibility: 'hidden' }}>
                            <Box sx={{ padding: '10px' }} />
                        </IconButton>
                    )}
                </Grid>
            </Grid>
        </>
    );
}
